"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Sparkles, Send, ShieldCheck, ThermometerSnowflake, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormOption {
  value: string;
  label: string;
}
interface FormFieldDef {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  default?: any;
  options?: FormOption[];
  config?: Record<string, any>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id?: number;
  key: string;
  name: string;
  description?: string;
  availability?: {
    accepting: boolean;
    state?: string;
    message?: string;
  };
  fields: FormFieldDef[];
  submit?: {
    label: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
  };
}
export default function Quote() {
  const prefersReducedMotion = useReducedMotion();
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [hpValue, setHpValue] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const fetchTimestampRef = useRef<number>(Date.now());
  const leadEventFiredRef = useRef<boolean>(false);
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    async function loadForm() {
      setIsLoadingForm(true);
      setLoadError(null);
      try {
        if (!client?.pluginRequest) {
          throw new Error("Client transport unavailable");
        }
        let def: FormDefinition | null = null;
        try {
          def = await client.pluginRequest("wvc-forms/v1/forms/request_a_quote");
        } catch (err: any) {
          if (err?.status === 404 || err?.code === "frms_not_found") {
            const listRes = await client.pluginRequest("wvc-forms/v1/forms");
            const forms: any[] = listRes?.items || [];
            const matched = forms.find((f: any) => f.key === "request_a_quote") || (forms.length === 1 ? forms[0] : null) || forms.find((f: any) => f.name?.toLowerCase().includes("quote") || f.name?.toLowerCase().includes("reservation"));
            if (matched?.key) {
              def = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            }
          } else {
            throw err;
          }
        }
        if (isMounted) {
          if (def && def.fields) {
            setFormDef(def);
            fetchTimestampRef.current = Date.now();
            const initialValues: Record<string, any> = {};
            def.fields.forEach(f => {
              if (f.type === "checkbox") {
                initialValues[f.id] = Array.isArray(f.default) ? f.default : [];
              } else if (f.type === "boolean") {
                initialValues[f.id] = f.default ? "1" : "";
              } else {
                initialValues[f.id] = f.default || "";
              }
            });
            setValues(initialValues);
          } else {
            setLoadError("Unable to initialize quotation service.");
          }
        }
      } catch {
        if (isMounted) {
          setLoadError("Quotation service is temporarily offline. Please call for immediate dispatch.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingForm(false);
        }
      }
    }
    loadForm();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleInputChange = (fieldId: string, val: any) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;
    leadEventFiredRef.current = false;
    setGeneralError(null);
    setFieldErrors({});
    const minSec = formDef.spam?.min_seconds ?? 3;
    const elapsedSec = (Date.now() - fetchTimestampRef.current) / 1000;
    if (elapsedSec < minSec) {
      await new Promise(r => setTimeout(r, (minSec - elapsedSec) * 1000));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    try {
      const payload: Record<string, any> = {
        values,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (!leadEventFiredRef.current) {
          if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
            (window as any).gtag("event", "generate_lead", {
              form_name: "request_a_quote",
              lead_type: "quote_request",
              page_location: window.location.href
            });
          }
          leadEventFiredRef.current = true;
        }
        if (res.confirmation?.type === "redirect" && res.confirmation.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml("<p>Your quote request was submitted successfully.</p><p>Fridge Fleet received your information. A representative will contact you using the information provided.</p>");
        }
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setGeneralError("Please correct the highlighted fields below.");
      } else if (res?.code === "frms_invalid_token") {
        try {
          const freshDef = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}`);
          if (freshDef?.spam?.token) {
            setFormDef(freshDef);
            fetchTimestampRef.current = Date.now();
            setGeneralError("Session refreshed. Please submit your request again.");
          }
        } catch {
          setGeneralError("Request timed out. Please refresh the page and try again.");
        }
      } else {
        setGeneralError(res?.message || "An unexpected error occurred. Please try again or call dispatch.");
      }
    } catch {
      setGeneralError("Network error. Please verify your connection or call directly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderField = (field: FormFieldDef, index: number) => {
    const errorObj = fieldErrors[field.id];
    const isRequired = field.required;
    const value = values[field.id] ?? "";
    if (field.type === "hidden") {
      return <input key={field.id} type="hidden" name={field.id} value={value} data-index={index} />;
    }
    if (field.type === "heading") {
      return <div key={field.id} className="col-span-full pt-4 pb-1" data-index={index}>
          <h3 className="text-base font-semibold tracking-tight text-foreground border-b border-border pb-2">
            {field.label}
          </h3>
        </div>;
    }
    if (field.type === "paragraph") {
      return <div key={field.id} className="col-span-full text-sm text-muted-foreground" data-index={index} dangerouslySetInnerHTML={{
        __html: field.config?.content || field.label
      }} />;
    }
    const isFullWidth = field.type === "textarea" || field.type === "checkbox" || field.type === "boolean" || field.key.includes("address") || field.key.includes("detail") || field.key.includes("consent") || field.key.includes("privacy") || field.key.includes("agree") || field.key.includes("terms");
    const isAgreementField = field.key.includes("agree") || field.key.includes("privacy") || field.key.includes("consent") || field.key.includes("terms") || field.label.toLowerCase().includes("agree") || field.label.toLowerCase().includes("privacy") || field.label.toLowerCase().includes("terms");
    return <div key={field.id} data-index={index} className={`flex flex-col gap-1.5 ${isFullWidth ? "col-span-full" : "col-span-full sm:col-span-1"}`}>
        <label htmlFor={field.id} className="text-sm font-medium text-foreground flex items-center justify-between">
          <span>
            {field.label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </span>
          {field.help && <span className="text-xs text-muted-foreground font-normal">
              {field.help}
            </span>}
        </label>

        {field.type === "select" && <Select value={value} onValueChange={val => handleInputChange(field.id, val)}>
            <SelectTrigger id={field.id} className={`w-full bg-background border-input text-foreground h-11 px-3 ${errorObj ? "border-destructive ring-1 ring-destructive/30" : ""}`}>
              <SelectValue placeholder={field.placeholder || "Select option"} />
            </SelectTrigger>
            <SelectContent className="bg-popover text-popover-foreground">
              {field.options?.map((opt, optIdx) => <SelectItem key={opt.value || optIdx} value={opt.value}>
                  {opt.label}
                </SelectItem>)}
            </SelectContent>
          </Select>}

        {field.type === "radio" && <RadioGroup value={value} onValueChange={val => handleInputChange(field.id, val)} className="flex flex-wrap gap-4 pt-1">
            {field.options?.map((opt, rIdx) => <div key={opt.value || rIdx} className="flex items-center space-x-2">
                <RadioGroupItem value={opt.value} id={`${field.id}_${rIdx}`} className="border-input text-primary" />
                <label htmlFor={`${field.id}_${rIdx}`} className="text-sm text-foreground cursor-pointer font-normal">
                  {opt.label}
                </label>
              </div>)}
          </RadioGroup>}

        {field.type === "textarea" && <Textarea id={field.id} value={value} onChange={e => handleInputChange(field.id, e.target.value)} placeholder={field.placeholder || "Provide specific delivery site requirements, target temperature, or power access details..."} rows={field.config?.rows || 4} className={`bg-background border-input text-foreground px-3 py-2 ${errorObj ? "border-destructive ring-1 ring-destructive/30" : ""}`} />}

        {(field.type === "boolean" || field.type === "checkbox" && (!field.options || field.options.length === 0)) && <div className="flex items-start space-x-3 pt-2">
            <Checkbox id={field.id} checked={value === "1" || value === true} onCheckedChange={checked => handleInputChange(field.id, checked ? "1" : "")} className={errorObj ? "border-destructive" : ""} />
            <label htmlFor={field.id} className="text-sm text-foreground leading-snug cursor-pointer font-normal">
              {isAgreementField ? <span>
                  I agree to the{" "}
                  <Link to="#" className="text-primary underline hover:text-primary/80 transition-colors">
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link to="#" className="text-primary underline hover:text-primary/80 transition-colors">
                    Privacy Policy
                  </Link>
                  , and consent to be contacted regarding this quote request.
                </span> : field.placeholder || field.label || "I agree to be contacted regarding this quote request"}
              {isRequired && <span className="text-destructive ml-1">*</span>}
            </label>
          </div>}

        {field.type !== "select" && field.type !== "radio" && field.type !== "textarea" && field.type !== "boolean" && (field.type !== "checkbox" || field.options && field.options.length > 0) && <Input id={field.id} type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "date" ? "date" : field.type === "number" ? "number" : "text"} value={value} onChange={e => handleInputChange(field.id, e.target.value)} placeholder={field.placeholder || ""} className={`bg-background border-input text-foreground h-11 px-3 ${errorObj ? "border-destructive ring-1 ring-destructive/30" : ""}`} />}

        {errorObj && <p className="text-xs text-destructive flex items-center gap-1 mt-0.5">
            <AlertCircle className="size-3.5 shrink-0" />
            <span>{errorObj.message}</span>
          </p>}
      </div>;
  };
  return <section id="request-a-quote-form" data-nav="light" className="relative bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/30 border border-accent text-accent-foreground text-xs md:text-sm font-semibold tracking-wider uppercase mb-4">
            <span className="size-2 rounded-full bg-chart-2 animate-pulse" />
            <span>QUOTE REQUEST</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-default text-foreground tracking-tight leading-tight mb-4">Request a Cold Storage Trailer Quote</h1>

          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto">Submit your rental details to request availability for a 6×8, 7×12, or 7×16 refrigerated or freezer trailer, or a <Link to="/FootRefrigeratedTrailerRentalsPage" className="text-primary underline hover:text-primary/80 transition-colors">53-foot reefer trailer rental</Link>. Coverage, trailer availability, specifications, delivery requirements, and scheduling are confirmed with each quote.</p>
        </div>

        {/* Dual Action Dispatch Top Banner */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-4 rounded-lg bg-card border border-border flex items-center gap-3.5 shadow-xs">
            <div className="size-10 rounded-md bg-muted flex items-center justify-center shrink-0 text-foreground">
              <ThermometerSnowflake className="size-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
                Rental Availability
              </p>
              <p className="text-sm font-medium text-foreground">
                Trailer availability and rental terms are confirmed with each quote.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-card border border-border flex items-center gap-3.5 shadow-xs">
            <div className="size-10 rounded-md bg-muted flex items-center justify-center shrink-0 text-foreground">
              <Sparkles className="size-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">Service Area</p>
              <p className="text-sm font-medium text-foreground">Listed Southern California and Las Vegas-area communities</p>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-card border border-border flex items-center gap-3.5 shadow-xs">
            <div className="size-10 rounded-md bg-muted flex items-center justify-center shrink-0 text-foreground">
              <Phone className="size-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">Call Fridge Fleet<div><br /></div></p>
              <p className="text-sm font-medium text-foreground">
                <a href="tel:2135218821" className="hover:underline">
                  213-521-8821
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Main Form Container Card */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }}>
          <Card className="bg-card text-card-foreground border border-border shadow-md rounded-xl p-6 sm:p-8 md:p-10">
            {isLoadingForm ? <div className="py-16 text-center space-y-4">
                <div className="inline-block size-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm font-medium text-muted-foreground">
                  Loading reservation request form...
                </p>
              </div> : confirmationHtml ? <div className="py-12 px-4 text-center max-w-xl mx-auto space-y-6">
                <div className="size-16 rounded-full bg-chart-2/20 text-chart-2 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="size-10" />
                </div>
                <div className="prose prose-sm md:prose-base text-foreground max-w-none [&_p]:leading-relaxed" dangerouslySetInnerHTML={{
              __html: confirmationHtml
            }} />
                <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <Button onClick={() => {
                leadEventFiredRef.current = false;
                setConfirmationHtml(null);
                setFieldErrors({});
                setGeneralError(null);
              }} variant="outline" className="border-border text-foreground">
                    Submit Another Request
                  </Button>
                  <span className="text-sm font-medium text-muted-foreground">
                    <a href="tel:2135218821" className="hover:underline">
                      213-521-8821
                    </a>
                  </span>
                </div>
              </div> : loadError ? <div className="py-12 text-center max-w-lg mx-auto space-y-4">
                <div className="size-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
                  <AlertCircle className="size-6" />
                </div>
                <p className="text-base font-medium text-foreground">{loadError}</p>
                <div className="pt-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    <a href="tel:2135218821" className="hover:underline">
                      213-521-8821
                    </a>
                  </p>
                </div>
              </div> : formDef ? <form onSubmit={handleSubmit} className="space-y-6">
                {formDef.spam?.honeypot?.field && <div style={{
              position: "absolute",
              left: "-9999px"
            }} aria-hidden="true">
                    <input type="text" name={formDef.spam.honeypot.field} value={hpValue} tabIndex={-1} autoComplete="off" onChange={e => setHpValue(e.target.value)} />
                  </div>}

                <CardHeader className="p-0 pb-6 border-b border-border">
                  <CardTitle className="text-xl md:text-2xl font-bold font-default text-foreground">
                    Rental Request Information
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    Provide the information below so we can review trailer availability, delivery requirements, and quote considerations.
                  </CardDescription>
                </CardHeader>

                {generalError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2.5">
                    <AlertCircle className="size-5 shrink-0 mt-0.5" />
                    <span>{generalError}</span>
                  </div>}

                <CardContent className="p-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                    {formDef.fields.map((field, idx) => renderField(field, idx))}
                  </div>
                </CardContent>

                <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="size-4 text-accent-foreground shrink-0" />
                    <span>Submitting this form does not guarantee trailer availability or suitability. Coverage, specifications, delivery requirements, rental terms, and scheduling are confirmed with each quote.</span>
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto sm:min-w-[200px] h-12 bg-primary text-primary-foreground font-semibold px-8 shadow-sm hover:bg-primary/90 transition-transform active:scale-98">
                    {isSubmitting ? <span className="flex items-center gap-2">
                        <span className="size-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                        {formDef.submit?.processing_label || "Processing..."}
                      </span> : <span className="flex items-center gap-2">
                        <Send className="size-4" />
                        {formDef.submit?.label || "Submit Quote Request"}
                      </span>}
                  </Button>
                </div>
              </form> : null}
          </Card>
        </motion.div>
      </div>
    </section>;
}